import {ChangeEventHandler, Dispatch, SetStateAction, useCallback} from 'react';
import {styled} from 'styled-components';

import {withThemeOverride} from '@shared_web/components/core/_base';
import {Label, LabelProps} from '@shared_web/components/core/label';
import {CustomWithout} from '@shared_web/lib/react';
import {cssPx, optional, optionalPx, optionalRaw} from '@shared_web/lib/styled_utils';

export interface CheckboxProps {
  syncState?:
    | Dispatch<SetStateAction<boolean>>
    | Dispatch<SetStateAction<boolean | undefined>>
    | ((value: boolean) => void)
    | ((value: boolean | undefined) => void);
  children: LabelProps['value'];
  width?: string | number;
}

const _Checkbox: CustomWithout<CheckboxProps, 'input', 'children' | 'size'> = ({
  syncState,
  children,
  checked,
  width,
  ...inputProps
}) => {
  const {disabled} = inputProps;

  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    evt => {
      syncState?.(evt.currentTarget.checked);
    },
    [syncState]
  );

  const LabelClass = disabled ? DisabledLabel : EnabledLabel;

  return (
    <LabelClass labelAfter labelWrap value={children} $width={width}>
      <Input type="checkbox" checked={checked ?? false} onChange={handleChange} {...inputProps} />
    </LabelClass>
  );
};

export const Checkbox = withThemeOverride(_Checkbox);
Checkbox.displayName = 'Checkbox';

const BaseLabel = styled(Label)<{$width?: string | number}>`
  display: flex;
  align-items: center;
  user-select: none;
  ${p => optionalPx('width', p.$width)};
  ${p => optionalPx('padding-top', p.theme.checkbox?.labelPaddingTop)}
  ${p => optionalPx('padding-right', p.theme.checkbox?.labelPaddingRight)}
  ${p => optionalPx('padding-bottom', p.theme.checkbox?.labelPaddingBottom)}
  ${p => optionalPx('padding-left', p.theme.checkbox?.labelPaddingLeft)}
  ${p => optionalRaw(p.theme.checkbox?.labelPaddingLeft, v => `margin-left: -${cssPx(v)};`)}
  ${p => optionalPx('border-radius', p.theme.checkbox?.labelBorderRadius)}
`;
const EnabledLabel = styled(BaseLabel)<{$width?: string | number}>`
  cursor: pointer;
  &:hover {
    ${p => optional('background-color', p.theme.checkbox?.labelHoverColor)}
  }
  border: solid 1px transparent;
  &:has(:focus-visible) {
    ${p =>
      optionalRaw(
        p.theme.checkbox?.backgroundColorChecked ?? p.theme.checkbox?.borderColorChecked,
        v => `border: solid 1px ${v};`
      )}
  }
`;
const DisabledLabel = styled(BaseLabel)<{$width?: string | number}>`
  opacity: 0.4;
`;

const defaultMarginRight = 8;
const defaultSize = 16;
const defaultBorderRadius = 4;

const Input = styled.input`
  appearance: none;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${p => p.theme.checkbox?.marginRight ?? defaultMarginRight}px;
  width: ${p => cssPx(p.theme.checkbox?.size ?? defaultSize)};
  height: ${p => cssPx(p.theme.checkbox?.size ?? defaultSize)};
  ${p => optional('color', p.theme.main?.textColor)};
  &:before {
    content: '';
    width: ${p => cssPx(p.theme.checkbox?.size ?? defaultSize)};
    height: ${p => cssPx(p.theme.checkbox?.size ?? defaultSize)};
    background-color: ${p => p.theme.checkbox?.backgroundColor ?? '#ffffff'};
    border-radius: ${p => cssPx(p.theme.checkbox?.borderRadius ?? defaultBorderRadius)};
    border-style: solid;
    ${p => optionalRaw(p.theme.checkbox?.borderColor, v => `border-color: ${v};`)}
    ${p => optionalPx('border-width', p.theme.checkbox?.borderWidth)}
  }
  &:checked:before {
    background-color: ${p => p.theme.checkbox?.backgroundColorChecked ?? '#ffffff'};
    ${p => optionalRaw(p.theme.checkbox?.borderColorChecked, v => `border-color: ${v};`)}
  }
`;
